<template>
  <BaseLayout :title="homework.title">
    <!-- Main Content (Center) -->
    <div class="container">
      <div v-if="loading" class="text-center my-4">
        <div class="spinner-border" role="status">
          <span class="sr-only">&nbsp;&nbsp;Loading...</span>
        </div>
      </div>
      <div v-else>
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">{{ homework.title }}</h5>
            <p class="mb-0 text-muted"><b>Release Date:</b> {{ formattedReleaseDate }}</p>
          </div>
          <div class="card-body">
            <!-- Render parsed markdown as HTML -->
            <!-- Deadlines Section -->
            <div class="card mb-3" v-if="homework.deadlines && homework.deadlines.length">
              <h5 class="card-header">Deadlines</h5>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-4 col-sm-12 mb-2">
                    <h6>Pre-evaluation</h6>
                    <div v-for="deadline in homework.deadlines.filter(d => d.evaluation_type === 'pre')" :key="deadline.id">
                      <p class="card-text"><strong>Date:</strong> {{ formatDate(deadline.date) }}</p>
                    </div>
                  </div>
                  <div class="col-md-4 col-sm-12 mb-2">
                    <h6>Submission</h6>
                    <div v-for="deadline in homework.deadlines.filter(d => d.evaluation_type === 'submission')" :key="deadline.id">
                      <p class="card-text"><strong>Date:</strong> {{ formatDate(deadline.date) }}</p>
                    </div>
                  </div>
                  <div class="col-md-4 col-sm-12 mb-2">
                    <h6>Post-evaluation</h6>
                    <div v-for="deadline in homework.deadlines.filter(d => d.evaluation_type === 'post')" :key="deadline.id">
                      <p class="card-text"><strong>Date:</strong> {{ formatDate(deadline.date) }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="card">
              <h5 class="card-header">Description</h5>
              <div class="card-body" v-html="formattedDescription"></div>
            </div>
            <div class="mb-3">
              <!-- Display only to teacher: Files Section -->
              <FilesList v-if="isTeacher && homework.file_paths && homework.file_paths.length"
                :title="'Homework Files'"
                :files="homework.file_paths"
                :onViewFile="showFileContent" 
                :onDownloadFile="downloadFile"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Sidebar Slot -->
    <template v-slot:right-sidebar>
      <!-- For student: download dataset -->
      <div v-if="!isTeacher" class="card p-3 mb-3">
        <h5 class="card-title">Dataset:</h5>
        <button @click="downloadDatasetForStudent" class="btn btn-success" :disabled="isDownloading" v-tooltip="'This action can take a while.'">
          <span v-if="isDownloading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          <i class="bi bi-download"></i> Download Dataset
        </button>
      </div>
      <!-- For student: open the AI assistant -->
      <div v-if="!isTeacher" class="card p-3 mb-3">
        <h5 class="card-title">Need help?</h5>
        <router-link to="/ask-ai" class="btn btn-outline-primary" v-tooltip="'Ask the AI assistant about this homework.'">
          <i class="bi bi-robot"></i> Ask AI
        </router-link>
      </div>
      <!-- For teacher: quick actions -->
      <div v-if="isTeacher" class="card p-3 mb-3">
        <h5 class="card-title">Actions:</h5>
        <router-link :to="`/homework/edit/${homework.id}`" class="btn btn-secondary mb-2">
          <i class="bi bi-pencil"></i> Edit Homework
        </router-link>
        <button @click="deleteHomework(homework.id)" class="btn btn-danger">
          <i class="bi bi-trash"></i> Delete Homework
        </button>
      </div>
      <!-- Download homework files -->
      <div class="card p-3 mb-3">
        <h5 class="card-title">Homework:</h5>
        <button @click="downloadAllHomeworkFilesForStudent" class="btn btn-success" v-tooltip="'Download all files necessary for completing this homework.'">
          <i class="bi bi-download"></i> Download Homework
        </button>
      </div>
      <!-- Routers to homework subtasks -->
      <div v-if="homework.subtasks && homework.subtasks.length" class="card p-3 mb-3"> 
        <h5 class="card-title">Tasks:</h5>
        <router-link v-for="subtask in sortedSubtasks" :key="subtask.id" :to="`/subtask/${subtask.id}`" class="btn btn-primary mb-2">
          <i class="bi bi-arrow-right-circle"></i> {{ subtask.title }}
        </router-link>
      </div>
    </template>

    <StemErrorModal ref="stemErrorModal"/>
  </BaseLayout>
</template>

<script>
import BaseLayout from '@/components/BaseLayout.vue';
import HomeworkController from '@/controllers/HomeworkController';
import SubtaskController from '@/controllers/SubtaskController';
import DatasetController from '@/controllers/DatasetController';
import FilesList from '@/components/FilesList.vue';
import Homework from '@/models/homework';
import Deadline from '@/models/deadline';
import StemErrorModal from '@/components/StemErrorModal.vue';
import { marked } from 'marked';
import { mapGetters } from 'vuex';

export default {
  name: 'HomeworkDetailView',
  components: {
    BaseLayout,
    FilesList,
    StemErrorModal,
  },
  data() {
    return {
      homework: {},
      loading: true,
      dropdownVisible: false,
      isDownloading: false
    };
  },
  computed: {
    ...mapGetters(['isTeacher', 'userId']),
    formattedDescription() {
      return marked.parse(this.homework.description || '');
    },
    formattedReleaseDate() {
      return Homework.formatDate(this.homework.release_date);
    },
    sortedSubtasks() {
      // Return sorted subtasks based on subtask.number in ascending order
      return (this.homework.subtasks || []).slice().sort((a, b) => a.number - b.number);
    },
  },
  async created() {
    try {
      const homeworkId = this.$route.params.id;
      this.homework = await HomeworkController.fetchHomeworkByIdWithRelatedData(homeworkId);
    } catch (error) {
      // In case of access error
      if (error.response && error.response.status === 403) {
        this.$store.dispatch('triggerAlert', {
          type: 'error',
          title: 'Error',
          message: 'You are not authorized to access this homework.',
        });
        this.$router.push('/homeworks');
        return;
      }
      this.$refs.stemErrorModal.show('Error', error.message);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    formatDate(datetime) {
      return Deadline.formatDateTime(datetime);
    },
    toggleDropdown() {
      this.dropdownVisible = !this.dropdownVisible;
    },
    async deleteHomework(homeworkId) {
      if (confirm('Are you sure you want to delete this homework?')) {
        try {
          await HomeworkController.deleteHomework(homeworkId);
          this.$store.dispatch('triggerAlert', {
            title: 'Success',
            message: 'Homework was deleted successfully',
            type: 'success',
          });
          this.$router.push('/homeworks');
        } catch (error) {
          this.$refs.stemErrorModal.show('Error', error.message);
        }
      }
    },
    async deleteSubtask(subtaskId) {
      if (confirm('Are you sure you want to delete this task?')) {
        try {
          await SubtaskController.deleteSubtask(subtaskId);
          this.$store.dispatch('triggerAlert', {
            title: 'Success',
            message: 'Task was deleted successfully',
            type: 'success',
          });
          this.homework.subtasks = this.homework.subtasks.filter(subtask => subtask.id !== subtaskId);
        } catch (error) {
          this.$refs.stemErrorModal.show('Error', error.message);
        }
      }
    },
    async downloadAllHomeworkFilesForStudent() {
      try {
        const response = await HomeworkController.downloadAllHomeworkFilesForStudent(this.homework.id);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${this.homework.title}.zip`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async showFileContent(filePath) {
      try {
        const response = await HomeworkController.downloadFile(filePath);
        // open file in new tab
        window.open(URL.createObjectURL(new Blob([response.data])));
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async downloadDatasetForStudent() {
      // only for students
      try {
        this.isDownloading = true;
        if (!this.isTeacher) {
          const studentId = this.userId;
          const dataset = await DatasetController.getDatasetForStudent(studentId);
          if (dataset && dataset.file_path) {
            await this.downloadFile(dataset.file_path);
          }
          else {
            this.$store.dispatch('triggerAlert', {
              title: 'Error',
              message: 'No dataset found. Contact teacher, please.',
              type: 'error',
            });
          }
        }
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.isDownloading = false;
      }
    },
    async downloadFile(filePath) {
      try {
        const response = await HomeworkController.downloadFile(filePath);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', this.getFileName(filePath));
        document.body.appendChild(link);
        link.click();
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    getFileName(filePath) {
      return filePath.split('/').pop();
    },
  },
};
</script>
